import axios, { STRAPI_URL } from '@propertypal/shared/src/services/axios.strapi';
import { StrapiResponse } from '@propertypal/shared/src/types/strapi/generic';
import { ContentHubArticle, ContentHubArticleCategory } from '@propertypal/shared/src/types/strapi/overrides';
import chSubCategorySSR, {
  ContentHubSubCategoryProps,
} from '@propertypal/shared/src/utils/content-hub/chSubCategorySSR';
import platform from '@propertypal/shared/src/utils/platform';
import ContentHubArticleGridResults from '@propertypal/web-ui/src/content-hub/ContentHubArticleGridResults';
import ContentHubHeaderCarousel from '@propertypal/web-ui/src/content-hub/ContentHubHeaderCarousel';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import React from 'react';
import BlogLayout from '../../components/layout/BlogLayout.pn';
import { ArticleGridContainer, ArticleGridWrapper } from '../../components/layout/BlogLayout.style.pn';
import generateMetaTags from '../../components/layout/MetaTags';

interface BlogProps extends ContentHubSubCategoryProps {
  trending: ContentHubArticle[] | null;
}

export const getServerSideProps: GetServerSideProps<BlogProps> = async ({ query }) => {
  const category: ContentHubArticleCategory = 'propertynews';
  const props = await chSubCategorySSR({
    category,
    page: (query.page as string | undefined) || '0',
    subCategories: { data: [] },
    subCategory: 'news',
  });

  const trending = await axios<StrapiResponse<ContentHubArticle[]>>({
    method: 'GET',
    url: `${STRAPI_URL}/articles`,
    params: {
      populate: 'deep,3',
      'filters[trending]': 'true',
      'filters[$and][0][$or][0][hidden][$eq]': 'false',
      'filters[$and][0][$or][1][hidden][$null]': 'true',
      'filters[$and][1][subCategories][articleCategory][$eq]': category,
      // Add platform filter for subCategories
      'filters[$and][2][subCategories][$or][0][platform][$eq]': 'Propertynews',
      'filters[$and][2][subCategories][$or][1][platform][$eq]': 'Both',
      // Add platform filter for category
      'filters[$and][3][$or][0][platform][$eq]': 'Propertynews',
      'filters[$and][3][$or][1][platform][$eq]': 'Both',
      // sort via publication date, so that the most recent appear first (descending)
      'sort[0]': 'publishedAt:desc',
      'pagination[page]': 1,
      // limit to 3 items
      'pagination[pageSize]': 3,
    },
  });

  return {
    props: {
      ...props,
      trending: trending.data?.data,
      disableHeader: true,
      disableFooter: true,
    },
  };
};

const Blog: React.FC<BlogProps> = ({ data, trending }) => {
  return (
    <>
      <Head>
        {generateMetaTags({
          title: `Northern Ireland Property News & Opinions - ${platform.title}.com`,
          description:
            'Get the latest news, opinions, analysis and advice on buying and selling in the Northern Ireland property market.',
          url: `/news`,
        })}
      </Head>

      <BlogLayout>
        {trending && trending.length > 0 && <ContentHubHeaderCarousel trending={trending} />}
        <ArticleGridContainer>
          <ArticleGridWrapper>
            <h2>Latest News from the Northern Ireland Property Market</h2>
            <ContentHubArticleGridResults
              articles={data?.data || []}
              url="/news"
              page={data?.meta?.pagination?.page || 1}
              totalPages={data?.meta?.pagination?.pageCount || 1}
            />
          </ArticleGridWrapper>
        </ArticleGridContainer>
      </BlogLayout>
    </>
  );
};

export default Blog;
